import {
  loadConfigRequest,
  loadConfigSuccess,
  loadConfigFailure,
  loadSchemaSuccess,
  saveConfigRequest,
  saveConfigSuccess,
  saveConfigFailure,
  setValidationErrors,
} from './searchExperienceSlice';
import {
  enqueueErrorSnackbar,
  enqueueWarningSnackbar,
} from '../../../actions/Notifications';
import { normalizeRuntimeSaveOutcome } from '../../../app/runtime/saveOutcome';
import { getLocalizedMessage } from '../../../util/useLocalizedNotifications';

function warningMessage(warning) {
  if (typeof warning === 'string') return warning;
  return warning?.message || warning?.error || null;
}

/**
 * Service for Search Experience v2 API
 * Uses the runtime-backed V2 settings client for backend communication.
 */
class SearchExperienceService {
  constructor(client, localizeMessage = getLocalizedMessage) {
    this.client = client;
    this.t = localizeMessage;
  }

  /**
   * Load config from backend
   * @param {string} shopId
   * @param {string} mode - 'preview' | 'production'
   * @param {function} dispatch - Redux dispatch
   */
  async loadConfig(shopId, mode, dispatch) {
    try {
      dispatch(loadConfigRequest());

      const config = await this.client.loadSearchExperienceConfig(shopId, mode);

      dispatch(loadConfigSuccess({ config, mode }));

      return config;
    } catch (error) {
      const errorMessage = error.message || this.t('search.settings.config.loadFailed');
      dispatch(loadConfigFailure({ error: errorMessage }));
      throw error;
    }
  }

  /**
   * Load config schema description
   * @param {string} shopId
   * @param {function} dispatch - Redux dispatch
   */
  async loadSchema(shopId, dispatch) {
    const schema = await this.client.describeSearchExperienceConfig(shopId);

    dispatch(loadSchemaSuccess({ schema }));

    return schema;
  }

  /**
   * Save config to backend
   * @param {string} shopId
   * @param {object} config
   * @param {string} mode - 'preview' | 'production'
   * @param {function} dispatch - Redux dispatch
   */
  async saveConfig(shopId, config, mode, dispatch) {
    try {
      dispatch(saveConfigRequest());

      const rawResult = await this.client.saveSearchExperienceConfig(shopId, config, mode);
      const result = normalizeRuntimeSaveOutcome(rawResult, config);

      if (!result.success || result.errors.length > 0) {
        dispatch(setValidationErrors({ errors: result.errors }));
        dispatch(saveConfigFailure({
          error: result.code === 'SETTINGS_CONFLICT'
            ? result.errors[0]?.message || this.t('search.settings.config.validationFailed')
            : this.t('search.settings.config.validationFailed'),
          ...(result.currentConfig ? { currentConfig: result.currentConfig } : {}),
        }));

        return {
          success: false,
          errors: result.errors,
          warnings: result.warnings,
          actions: result.actions,
        };
      }

      dispatch(saveConfigSuccess({ config: result.config }));
      result.warnings
        .map(warningMessage)
        .filter(Boolean)
        .forEach(message => dispatch(enqueueWarningSnackbar(message, 5000)));

      return {
        success: true,
        errors: [],
        warnings: result.warnings,
        actions: result.actions,
        config: result.config,
      };
    } catch (error) {
      const validationErrors = SearchExperienceService._extractValidationErrors(error);

      if (validationErrors.length > 0) {
        dispatch(setValidationErrors({ errors: validationErrors }));
        dispatch(saveConfigFailure({ error: this.t('search.settings.config.validationFailed') }));

        return {
          success: false,
          errors: validationErrors,
        };
      }

      const errorMessage = error.message || this.t('search.settings.config.saveFailed');
      dispatch(saveConfigFailure({ error: errorMessage }));
      dispatch(enqueueErrorSnackbar(errorMessage));

      return {
        success: false,
        error: errorMessage,
      };
    }
  }

  /**
   * Extract validation errors from different error formats
   * @private
   */
  static _extractValidationErrors(error) {
    const validationErrors = [];

    if (error.response?.errors) {
      return Array.isArray(error.response.errors)
        ? error.response.errors
        : [error.response.errors];
    }

    if (error.errors) {
      return Array.isArray(error.errors) ? error.errors : [error.errors];
    }

    if (error.response?.data?.errors) {
      return Array.isArray(error.response.data.errors)
        ? error.response.data.errors
        : [error.response.data.errors];
    }

    if (error.message && error.message.includes('validation')) {
      return [{
        path: 'config',
        message: error.message,
        type: 'validation_error',
      }];
    }

    return validationErrors;
  }

  /**
   * Validate config on backend
   * @param {string} shopId
   * @param {object} config
   * @param {function} dispatch - Redux dispatch
   */
  async validateConfig(shopId, config, dispatch) {
    const validateLocally = () => {
      const errors = [];

      if (!config.schemaVersion) {
        errors.push({ field: 'schemaVersion', message: 'Schema version is required' });
      }

      if (!config.search) {
        errors.push({ field: 'search', message: 'Search config is required' });
      }

      dispatch(setValidationErrors({ errors }));
      return { valid: errors.length === 0, errors };
    };

    // Plugin gateway validates the canonical payload during save, but has no
    // read-only validation route. Keep preflight useful without invoking an
    // unsupported runtime command; save remains the authoritative validation.
    if (typeof this.client.validateSearchExperienceConfig !== 'function') {
      return validateLocally();
    }

    try {
      // Use backend validation endpoint
      const result = await this.client.validateSearchExperienceConfig(shopId, config);

      dispatch(setValidationErrors({ errors: result.errors || [] }));

      return result;
    } catch (error) {
      // Check if response has validation errors
      if (error.response?.error === 'VALIDATION_FAILED' && error.response?.errors) {
        dispatch(setValidationErrors({ errors: error.response.errors }));
        return {
          valid: false,
          errors: error.response.errors,
        };
      }

      // A runtime that advertises remote validation must not silently turn an
      // authorization, network, or server failure into a successful local
      // validation. Local validation is reserved for adapters that explicitly
      // do not expose a validation operation (currently the plugin gateway).
      throw error;
    }
  }

  /**
   * Copy preview config to production
   * @param {string} shopId
   * @param {function} dispatch - Redux dispatch
   */
  async copyToProduction(shopId, dispatch) {
    try {
      dispatch(saveConfigRequest());

      const result = await this.client.copySearchExperienceToProduction(shopId);

      dispatch(saveConfigSuccess({ config: result.config }));

      return result;
    } catch (error) {
      const errorMessage = error.message || this.t('search.settings.config.copyToProductionFailed');
      dispatch(saveConfigFailure({ error: errorMessage }));
      throw error;
    }
  }

  /**
   * Get config diff (changes between original and current)
   * @param {object} original
   * @param {object} current
   * @returns {object} Diff object
   */
  // eslint-disable-next-line class-methods-use-this
  getConfigDiff(original, current) {
    const diff = {};

    const findDiff = (path, origVal, currVal) => {
      if (JSON.stringify(origVal) !== JSON.stringify(currVal)) {
        diff[path] = { original: origVal, current: currVal };
      }
    };

    const traverse = (orig, curr, path = '') => {
      if (!orig || !curr) return;

      const keys = new Set([...Object.keys(orig || {}), ...Object.keys(curr || {})]);

      keys.forEach((key) => {
        const newPath = path ? `${path}.${key}` : key;
        const origVal = orig?.[key];
        const currVal = curr?.[key];

        if (typeof origVal === 'object' && typeof currVal === 'object' && !Array.isArray(origVal)) {
          traverse(origVal, currVal, newPath);
        } else {
          findDiff(newPath, origVal, currVal);
        }
      });
    };

    traverse(original, current);

    return {
      hasChanges: Object.keys(diff).length > 0,
      changes: diff,
      changedFields: Object.keys(diff),
    };
  }
}

export default SearchExperienceService;
